import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getEntry } from "core-integration/src/store/entries/reducers";
import { getRules } from "core-integration/src/store/game/reducers";
import {
  getJoinPrivateLeagueError,
  getJoinPrivateLeagueSuccess,
} from "core-integration/src/store/leagues/reducers";
import { joinPrivateLeague } from "core-integration/src/store/leagues/thunks";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { ILoggedInPlayer } from "core-integration/src/store/player/types";
import { ErrorAlert } from "plos/src/components/alerts";
import { Button } from "plos/src/components/buttons/Button";
import { FormTextField } from "plos/src/components/forms";
import SectionHeader from "plos/src/components/SectionHeader";
import { SurfaceContainer } from "plos/src/components/SurfaceContainer";
import { useLocalizedNavigate } from "plos/src/hooks/useLocalizedNavigate";
import { rightSidebarLayout } from "plos/src/layouts";
import { SyntheticEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useTrackingContext } from "../../../contexts/TrackingContext";
import { formatErrorMsg, getErrorType } from "../../leagues/errors";
import HelmetHeadLeagues from "../../leagues/HelmetHeadLeagues";
import {
  formItem,
  leagueGridLayout,
  leaguesMainContent,
} from "../leaguesAndCups.css";

const JoinPrivate = () => {
  const { t } = useTranslation("leaguesAndCups");
  const [code, setCode] = useState("");
  const { fireClickTrackEvent, firePageViewEvent, logBranchEvent } =
    useTrackingContext();
  const dispatch = useDispatch<ThunkDispatch>();
  const navigate = useLocalizedNavigate();

  const player = useSelector(
    (state: RootState) => getPlayerData(state) as ILoggedInPlayer,
  ); // enforced by EntryRoute
  const playerEntry = player.entry;
  const entry = useSelector((state: RootState) => getEntry(state, playerEntry));
  const error = useSelector(getJoinPrivateLeagueError);
  const rules = useSelector(getRules);
  const hasJoinedPrivate = useSelector(getJoinPrivateLeagueSuccess);

  useEffect(() => {
    firePageViewEvent("fantasy leagues & cups", "join private league");
  }, []);

  const miniLeagueJoinedEventData = {
    event_category: "fantasy league",
    event_component: "fantasy classic clicks",
    event_detail: "join invitational cta",
    event_type: "join invitational league / cup",
  };

  useEffect(() => {
    if (!hasJoinedPrivate) return;

    logBranchEvent("Mini-league joined", miniLeagueJoinedEventData);
    navigate("/leagues");
  }, [hasJoinedPrivate, navigate]);

  const handleCodeChange = (val: string) => {
    setCode(val);
  };

  const handleSubmit = (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    dispatch(joinPrivateLeague({ code }));
    fireClickTrackEvent(
      miniLeagueJoinedEventData,
      "fantasy join private league",
    );
  };

  const disabled = !code;
  const errorType = getErrorType(error);

  if (!entry) {
    return null;
  }

  return (
    <>
      <HelmetHeadLeagues />
      <div className={rightSidebarLayout}>
        <SurfaceContainer>
          <div className={leaguesMainContent}>
            {errorType && rules && (
              <ErrorAlert>{formatErrorMsg(errorType, rules)}</ErrorAlert>
            )}
            <SectionHeader
              title={t(
                "joinPrivate.invitationalLeagues.heading",
                "Join an Invitational League & Cup",
              )}
            />
            <form onSubmit={handleSubmit}>
              <div className={leagueGridLayout}>
                <div className={formItem}>
                  <FormTextField
                    label={t(
                      "joinPrivate.invitationalLeagues.leagueCode",
                      "League Code",
                    )}
                    onChange={handleCodeChange}
                    value={code}
                    placeholder={
                      t(
                        "joinPrivate.invitationalLeagues.enterLeagueCode",
                        "Enter league code",
                      ) as string
                    }
                    maxLength={20}
                  />
                </div>
                <div className={formItem}>
                  <Button type="submit" isDisabled={disabled}>
                    {t(
                      "joinPrivate.invitationalLeagues.joinLeagueButton",
                      "Join League",
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </SurfaceContainer>
      </div>
    </>
  );
};

export default JoinPrivate;
